import { useMutation, useQuery } from "@tanstack/react-query";
import {
  fetchQuizQuestionNumberList,
  fetchQuizQuestions,
  saveQuizGuessAnswer,
  saveQuizAssessmentSubmitStatus,
  saveQuizQuestionAnswer,
  submitQuiz,
  storeQuizQuestionTime,
  toggleQuizPause,
} from "../services/quizQuestionService";

function extractPagination(payload = {}) {
  const pagination = payload?.pagination || {};
  const currentPage = Number(pagination?.currentPage || pagination?.current_page || payload?.current_page || 1);
  const totalPages = Number(
    pagination?.totalPages ||
      pagination?.total_pages ||
      pagination?.lastPage ||
      pagination?.last_page ||
      payload?.total_pages ||
      payload?.last_page ||
      1
  );
  const explicitNextPage =
    Number(pagination?.nextPage || pagination?.next_page || payload?.next_page || 0) || 0;
  const explicitPrevPage =
    Number(
      pagination?.prevPage ||
        pagination?.prev_page ||
        pagination?.previousPage ||
        pagination?.previous_page ||
        payload?.prev_page ||
        payload?.previous_page ||
        0
    ) || 0;

  return {
    currentPage,
    perPage: Number(pagination?.perPage || pagination?.per_page || payload?.per_page || 0),
    totalRecords: Number(
      pagination?.totalRecords ||
        pagination?.total_records ||
        pagination?.total ||
        payload?.total_records ||
        payload?.total ||
        payload?.question_count ||
        payload?.total_questions ||
        0
    ),
    totalPages,
    nextPage: explicitNextPage || (currentPage < totalPages ? currentPage + 1 : 0),
    prevPage: explicitPrevPage || (currentPage > 1 ? currentPage - 1 : 0),
    previousPage: explicitPrevPage || (currentPage > 1 ? currentPage - 1 : 0),
  };
}

function getLocalizedText(item = {}, baseField) {
  return item?.[`${baseField}_en`] || item?.[baseField] || item?.[`${baseField}_hi`] || "";
}

function isAffirmativeFlag(value) {
  if (value === true) {
    return true;
  }

  const normalized = String(value ?? "").trim().toUpperCase();
  return normalized === "1" || normalized === "Y" || normalized === "YES" || normalized === "TRUE";
}

function normalizeOption(option = {}, index = 0) {
  return {
    id: Number(option?.option_id || option?.id || index + 1),
    label: getLocalizedText(option, "option_name") || `Option ${index + 1}`,
    isCorrect: String(option?.is_option_correct || "").toUpperCase() === "Y",
    isSelected: Number(option?.user_selected_option || 0) === 1,
  };
}

function normalizeQuestion(item = {}, index = 0, resolvedSequence = 0) {
  const questionId = Number(item?.question_id || item?.id || item?.qb_question_id || index + 1);
  const selectedOptionId =
    Number(
      item?.selected_option_id ||
        item?.user_selected_option_id ||
        item?.question_option?.find((option) => Number(option?.user_selected_option || 0) === 1)
          ?.option_id ||
        0
    ) || null;
  const normalizedSequence = Number(
    resolvedSequence || item?.question_index || index + 1
  );

  return {
    id: questionId,
    question_id: questionId,
    serialNo: normalizedSequence,
    question_index: normalizedSequence,
    question:
      getLocalizedText(item, "question_name") ||
      item?.question ||
      item?.question_html ||
      item?.question_text ||
      item?.question_title ||
      "Question will be available soon.",
    questionHtml:
      getLocalizedText(item, "question_name") ||
      item?.question ||
      item?.question_html ||
      item?.question_text ||
      item?.question_title ||
      "Question will be available soon.",
    questionImage: item?.question_image || "",
    questionStatementOne: getLocalizedText(item, "question_statement_one"),
    questionStatementTwo: getLocalizedText(item, "question_statement_two"),
    options: Array.isArray(item?.question_option) ? item.question_option.map(normalizeOption) : [],
    totalQuestions: Number(item?.total_questions || 0),
    isAnswered: Number(item?.is_question_answered || item?.question_answered || item?.is_answered || 0) === 1,
    isGuess: Number(item?.is_guess || item?.is_guess_answer || item?.guess_status || 0) === 1,
    isLast: Number(item?.is_last || 0) === 1,
    isFirst: Number(item?.is_first || 0) === 1,
    isBookmarked: Number(item?.bookmark_status || item?.is_bookmarked || 0) === 1,
    isLiked: Number(item?.like_status || item?.is_liked || 0) === 1,
    selectedOptionId,
    likeCount: Number(item?.total_likes || item?.like_count || 0),
    viewCount: Number(item?.total_views || item?.view_count || 0),
    reportCount: Number(item?.total_reports || item?.report_count || 0),
    notesCount: Number(item?.total_notes || item?.notes_count || 0),
    questionTime: Number(item?.question_time || 0),
    refMessage: getLocalizedText(item, "ref_message"),
    rightExplanation: getLocalizedText(item, "right_explanation"),
    pyqMappings: Array.isArray(item?.pyq_mappings) ? item.pyq_mappings : [],
    raw: item,
  };
}

function normalizePaletteQuestion(item = {}, index = 0) {
  const sequence = Number(
    item?.question_index || item?.question_number || item?.count || item?.q_index || index + 1
  );
  const questionId = Number(item?.question_id || item?.id || 0);

  return {
    id: questionId || sequence,
    question_id: questionId,
    serialNo: sequence,
    question_index: sequence,
    question_number: sequence,
    count: sequence,
    question:
      getLocalizedText(item, "question_name") ||
      item?.question ||
      item?.name ||
      `Question ${sequence}`,
    questionHtml:
      getLocalizedText(item, "question_name") ||
      item?.question ||
      item?.name ||
      `Question ${sequence}`,
    isAnswered:
      isAffirmativeFlag(item?.is_question_answered) ||
      isAffirmativeFlag(item?.is_answered) ||
      isAffirmativeFlag(item?.question_answered) ||
      isAffirmativeFlag(item?.attempted),
    isCorrect:
      item?.is_answer_correct === null ||
      item?.is_answer_correct === undefined ||
      item?.is_answer_correct === ""
        ? null
        : isAffirmativeFlag(item?.is_answer_correct),
    raw: item,
  };
}

export function useQuizPracticeQuestions(params = {}, options = {}) {
  return useQuery({
    queryKey: ["quizPracticeQuestions", params],
    queryFn: async () => {
      const { payload } = await fetchQuizQuestions(params);
      const pagination = extractPagination(payload);
      const rawQuestions =
        payload?.questions ||
        payload?.question_list ||
        payload?.questions_list ||
        payload?.data ||
        [];
      const questionList = Array.isArray(rawQuestions) ? rawQuestions : [];
      const currentPage = Number(pagination?.currentPage || params?.page || 1);
      const pageSize = Number(pagination?.perPage || questionList.length || 0);
      const hasPageLocalQuestionIndex =
        currentPage > 1 &&
        questionList.length > 0 &&
        questionList.every((item, index) => {
          const rawSequence = Number(item?.question_index || index + 1);
          return rawSequence === index + 1;
        });

      return {
        quizMode: String(payload?.quiz_mode || params?.quiz_mode || "P").trim().toUpperCase(),
        durations: Number(payload?.durations || payload?.duration || 0),
        isPaused: Number(payload?.is_paused || 0) === 1,
        pauseEnabled: Number(payload?.pause_enabled || 0) === 1,
        totalQuestions:
          Number(payload?.total_questions || payload?.question_count || payload?.pagination?.totalRecords || 0) ||
          Number(questionList?.[0]?.total_questions || 0),
        pagination,
        questions: questionList.map((item, index) => {
          const resolvedSequence = hasPageLocalQuestionIndex
            ? ((currentPage - 1) * Number(pageSize || questionList.length || 1)) + index + 1
            : Number(item?.question_index || index + 1);
          return normalizeQuestion(item, index, resolvedSequence);
        }),
      };
    },
    enabled: options.enabled ?? true,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
    placeholderData: (previousData) =>
      previousData ?? {
        quizMode: String(params?.quiz_mode || "P").trim().toUpperCase(),
        durations: 0,
        isPaused: false,
        pauseEnabled: false,
        totalQuestions: 0,
        pagination: {
          currentPage: 1,
          perPage: 0,
          totalRecords: 0,
          totalPages: 1,
          nextPage: 0,
          prevPage: 0,
          previousPage: 0,
        },
        questions: [],
      },
  });
}

export function useQuizPracticePalette(params = {}, options = {}) {
  return useQuery({
    queryKey: ["quizPracticePalette", params],
    queryFn: async () => {
      const { payload } = await fetchQuizQuestionNumberList(params);
      const rawItems =
        payload?.questions ||
        payload?.question_list ||
        payload?.questions_list ||
        payload?.question_number_list ||
        payload?.data ||
        [];

      return {
        totalQuestions: Number(payload?.total_questions || 0),
        solvedCount: Number(payload?.total_answered || 0),
        unsolvedCount: Number(payload?.not_answered || 0),
        correctCount: Number(payload?.correct_questions || 0),
        incorrectCount: Number(payload?.incorrect_questions || 0),
        unvisitedQuestions: Number(payload?.unvisited_questions || 0),
        skippedQuestions: Number(payload?.skipped_questions || 0),
        guessScore: Number(payload?.guess_score || 0),
        items: Array.isArray(rawItems) ? rawItems.map(normalizePaletteQuestion) : [],
        raw: payload,
      };
    },
    enabled: options.enabled ?? true,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
    placeholderData: (previousData) =>
      previousData ?? {
        totalQuestions: 0,
        solvedCount: 0,
        unsolvedCount: 0,
        correctCount: 0,
        incorrectCount: 0,
        unvisitedQuestions: 0,
        skippedQuestions: 0,
        guessScore: 0,
        items: [],
        raw: {},
      },
  });
}

export function useQuizPracticeMutations() {
  return {
    saveAnswer: useMutation({
      mutationFn: saveQuizQuestionAnswer,
    }),
    saveTime: useMutation({
      mutationFn: storeQuizQuestionTime,
    }),
    saveGuess: useMutation({
      mutationFn: saveQuizGuessAnswer,
    }),
    submitQuiz: useMutation({
      mutationFn: submitQuiz,
    }),
    saveAssessmentSubmitStatus: useMutation({
      mutationFn: saveQuizAssessmentSubmitStatus,
    }),
    togglePauseQuiz: useMutation({
      mutationFn: toggleQuizPause,
    }),
  };
}
